import { createContext, useContext, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useStytch, useStytchUser } from "@stytch/react";
import { registerApiAuth } from "@/lib/api";
import { applyActAs } from "@/lib/testActAs";
import { freshSessionJwt, forceSessionRefresh } from "@/lib/stytch-session";
import { ApiRequestError, announcePlanLimit, isPlanLimitCode } from "@/lib/planLimit";
import { announceAccountClosed } from "@/lib/accountClosed";
import { announceTwoStepRequired } from "@/lib/twoStep";

const API_BASE = "/api";

interface ApiError {
  message: string;
  code?: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      message: response.status === 401 ? "Unauthorized" : "An unexpected error occurred",
    }));
    if (isPlanLimitCode(error.code)) announcePlanLimit(error.code);
    if (error.code === "ACCOUNT_CLOSED") announceAccountClosed();
    if (error.code === "TWO_STEP_REQUIRED") announceTwoStepRequired();
    throw new ApiRequestError(error.message, error.code, response.status);
  }
  // 204 No Content has no body to parse: response.json() would throw on a
  // request that succeeded, and callers skipped everything after it.
  if (response.status === 204) return undefined as T;
  return response.json();
}

type FetchWithAuth = (url: string, options?: RequestInit) => Promise<Response>;

interface ApiContextValue {
  fetchWithAuth: FetchWithAuth;
  isSignedIn: boolean | undefined;
}

const ApiContext = createContext<ApiContextValue | null>(null);

/**
 * Stytch's SDK handles session refresh transparently — `getTokens()`
 * always returns whatever the SDK considers current. No expiry math,
 * no manual refresh calls. The 401 retry below covers edge cases
 * (clock skew, key rotation) by re-reading the token, which will
 * have been refreshed by the SDK in the meantime.
 */
export function ApiProvider({ children }: { children: ReactNode }) {
  const stytch = useStytch();
  const { user, isInitialized } = useStytchUser();
  const isAuthenticated = !!user;
  const isLoading = !isInitialized;

  // Keep stytch in a ref so callbacks stay identity-stable even if
  // the hook returns a fresh reference each render.
  const stytchRef = useRef(stytch);
  stytchRef.current = stytch;

  const getValidToken = useCallback(async (): Promise<string | undefined> => {
    // Renews before use when the JWT is near expiry, rather than waiting for a
    // 401 to tell us. See lib/stytch-session.ts.
    return freshSessionJwt(stytchRef.current as never);
  }, []);

  // Register a non-hook token provider on the api module so `src/lib/api.ts`
  // (which uses raw fetch from outside React) can also send authenticated
  // requests. The refresh callback used to be a 200ms sleep, on the belief that
  // the SDK had no forced-refresh API. It does — session.authenticate() — so
  // this now actually mints a new JWT instead of hoping one appeared.
  // Registered during the first render, not in an effect: React runs the children's effects before
  // this provider's, so the first queries (the notification bell's) went out with no token, got a
  // 401 and forced a session refresh on every page load (28 Oct).
  const registered = useRef(false);
  if (!registered.current) {
    registered.current = true;
    registerApiAuth(
      async () => (await getValidToken()) ?? null,
      async () => {
        await forceSessionRefresh(stytchRef.current as never);
      }
    );
  }

  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      if (isLoading) {
        throw new Error("Authentication not ready");
      }

      const buildHeaders = (token: string | undefined) => {
        const headers = applyActAs(new Headers(options.headers));
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const isFormData = options.body instanceof FormData;
        if (!headers.has("Content-Type") && options.body && !isFormData) {
          headers.set("Content-Type", "application/json");
        }
        return headers;
      };

      let token = await getValidToken();
      let response = await fetch(url, {
        ...options,
        headers: buildHeaders(token),
        credentials: "include",
      });

      // 401 recovery: mint a fresh JWT and retry once. Still worth keeping
      // even with the proactive check above — a token can be rejected for
      // reasons its `exp` does not show, such as a revoked session.
      if (response.status === 401 && isAuthenticated) {
        await forceSessionRefresh(stytchRef.current as never);
        token = await getValidToken();
        response = await fetch(url, {
          ...options,
          headers: buildHeaders(token),
          credentials: "include",
        });
      }

      return response;
    },
    [getValidToken, isLoading, isAuthenticated]
  );

  const value = useMemo(
    () => ({ fetchWithAuth, isSignedIn: isAuthenticated }),
    [fetchWithAuth, isAuthenticated]
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApiContext() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
}

/** The API context, or null outside an ApiProvider (isolated views and their tests). */
export function useOptionalApiContext() {
  return useContext(ApiContext);
}

export type { FetchWithAuth };
export { API_BASE, handleResponse };
